import { Search } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { blogService } from "../../../api/services/blogService";

const BlogList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 9;

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogService.getBlogs({
        limit,
        offset: (page - 1) * limit,
        search: search || undefined,
        category: category || undefined,
      });
      setArticles(response.data?.articles || []);
      setTotalPages(Math.ceil((response.data?.count || 0) / limit));
    } catch (error) {
      console.error("Failed to load articles:", error);
    } finally {
      setLoading(false);
    }
  }, [page, search, category]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await blogService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  }, []);

  useEffect(() => {
    loadArticles();
    loadCategories();
  }, [loadArticles, loadCategories]);

  const handleArticleClick = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCategoryClick = (cat: string) => {
    setCategory(cat === category ? "" : cat);
    setPage(1);
  };

  if (loading && articles.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Blog & Resources
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Insights and updates for healthcare professionals
        </Typography>
      </Box>

      {/* Search & Filters */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search articles..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
          size="small"
        />

        {categories.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label="All"
              onClick={() => handleCategoryClick("")}
              color={category === "" ? "primary" : "default"}
              sx={{ cursor: "pointer" }}
              size="small"
            />
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => handleCategoryClick(cat)}
                color={category === cat ? "primary" : "default"}
                sx={{ cursor: "pointer" }}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Articles Grid */}
      {articles.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No articles found
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {articles.map((article: any) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => handleArticleClick(article.slug)}
                >
                  {article.featured_image && (
                    <CardMedia
                      component="img"
                      height="180"
                      image={article.featured_image}
                      alt={article.title}
                      sx={{ objectFit: "cover" }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    {article.category && (
                      <Chip
                        label={article.category}
                        size="small"
                        color="primary"
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {article.title}
                    </Typography>
                    {article.excerpt && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {article.excerpt}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: "auto",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {new Date(article.published_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.view_count} views
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default BlogList;
